import type { GameState, Orientation } from "./types";

export const key = (col: number, row: number) => `${col}:${row}`;
export const parseKey = (value: string): [number, number] => value.split(":").map(Number) as [number, number];

export function directedNeighbor(orientation: Orientation, col: number, row: number, direction: number): [number, number] {
  const dir = ((direction % 6) + 6) % 6;
  if (orientation === "POINTY_TOP") {
    const even = [[1, 0], [0, -1], [-1, -1], [-1, 0], [-1, 1], [0, 1]];
    const odd = [[1, 0], [1, -1], [0, -1], [-1, 0], [0, 1], [1, 1]];
    const [dc, dr] = (row & 1 ? odd : even)[dir];
    return [col + dc, row + dr];
  }
  const even = [[1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [0, 1]];
  const odd = [[1, 1], [1, 0], [0, -1], [-1, 0], [-1, 1], [0, 1]];
  const [dc, dr] = (col & 1 ? odd : even)[dir];
  return [col + dc, row + dr];
}

export function inside(state: GameState, col: number, row: number): boolean {
  return col >= 0 && row >= 0 && col < state.width && row < state.height && state.terrain[row][col] !== "VOID";
}

export function neighbors(state: GameState, col: number, row: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let d = 0; d < 6; d++) {
    const n = directedNeighbor(state.orientation, col, row, d);
    if (inside(state, n[0], n[1])) out.push(n);
  }
  return out;
}

export function hexDistance(orientation: Orientation, colA: number, rowA: number, colB: number, rowB: number): number {
  if (orientation === "POINTY_TOP") {
    const xA = colA - (rowA - (rowA & 1)) / 2;
    const xB = colB - (rowB - (rowB & 1)) / 2;
    const zA = rowA, zB = rowB;
    return Math.max(Math.abs(xA - xB), Math.abs((-xA - zA) - (-xB - zB)), Math.abs(zA - zB));
  }
  const zA = rowA - (colA - (colA & 1)) / 2;
  const zB = rowB - (colB - (colB & 1)) / 2;
  return Math.max(Math.abs(colA - colB), Math.abs((-colA - zA) - (-colB - zB)), Math.abs(zA - zB));
}

export const distance = (state: GameState, aCol: number, aRow: number, bCol: number, bRow: number) =>
  hexDistance(state.orientation, aCol, aRow, bCol, bRow);

export function cellCenter(orientation: Orientation, col: number, row: number, radius: number): [number, number] {
  if (orientation === "POINTY_TOP") {
    return [radius * Math.sqrt(3) * (col + 0.5 * (row & 1)), radius * 1.5 * row];
  }
  return [radius * 1.5 * col, radius * Math.sqrt(3) * (row + 0.5 * (col & 1))];
}
